import type {
  PatternDetailsResponse,
  PatternSearchResult,
  PatternVersionResponse,
  PaginatedAuthorPatternsResponse,
  PaginatedNewestPatternsFullResponse,
  SearchPatternsFilteredResponse,
  SearchResponse,
  SearchProjectsResponse,
  MaterialPagePatternsResponse,
  MaterialPagePatternUsage,
  ProjectSearchDocument,
} from '@mrb91002/shared-types'

import { API_BASE } from '../config'

export async function fetchNewestPatternsFull(
  limit = 5,
  lastKey: Record<string, unknown> | string | null = null,
): Promise<PaginatedNewestPatternsFullResponse> {
  let url = `${API_BASE}/patterns/public/full?limit=${limit}`
  if (lastKey) {
    const lastKeyStr = typeof lastKey === 'string' ? lastKey : JSON.stringify(lastKey)
    url += `&lastKey=${encodeURIComponent(lastKeyStr)}`
  }
  const resp = await fetch(url)
  if (!resp.ok) throw new Error('Failed to fetch newest patterns (full)')
  return resp.json() as Promise<PaginatedNewestPatternsFullResponse>
}

export async function searchPatternsWithFilters(
  sortBy: 'newest' | 'highest-rated' | 'most-reviewed',
  dateRange: 'day' | 'week' | 'month' | 'year' | 'all',
  limit = 50,
  from = 0,
): Promise<SearchPatternsFilteredResponse> {
  const url = `${API_BASE}/patterns/search-filtered?sortBy=${sortBy}&dateRange=${dateRange}&limit=${limit}&from=${from}`
  const resp = await fetch(url)
  if (!resp.ok) throw new Error('Failed to search patterns')
  return resp.json() as Promise<SearchPatternsFilteredResponse>
}

export async function fetchPatternDetails(
  patternId: string,
  includeUserReview: boolean = false,
  idToken?: string,
): Promise<PatternDetailsResponse> {
  const url = `${API_BASE}/patterns/${patternId}/details${includeUserReview ? '?includeUserReview=true' : ''}`
  const resp = await fetch(url, {
    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
  })
  if (!resp.ok) throw new Error('Failed to fetch pattern details')
  return resp.json() as Promise<PatternDetailsResponse>
}

export async function fetchPatternVersion(patternId: string, versionNumber: number): Promise<PatternVersionResponse> {
  const url = `${API_BASE}/pattern/${encodeURIComponent(patternId)}/version/${versionNumber}`
  const resp = await fetch(url)
  if (!resp.ok) throw new Error('Failed to fetch pattern version')
  return resp.json() as Promise<PatternVersionResponse>
}

export async function fetchAuthorPatterns(
  authorId: string,
  lastKey: string | null = null,
): Promise<PaginatedAuthorPatternsResponse> {
  let url = `${API_BASE}/author/${encodeURIComponent(authorId)}/patterns`
  if (lastKey) url += `?lastKey=${encodeURIComponent(lastKey)}`
  console.log('[PATTERNS] fetchAuthorPatterns:', { authorId, lastKey, url })
  const resp = await fetch(url)
  console.log('[PATTERNS] fetchAuthorPatterns response:', { status: resp.status, ok: resp.ok })
  if (!resp.ok) throw new Error('Failed to fetch author patterns')
  const data = await resp.json() as PaginatedAuthorPatternsResponse
  console.log('[PATTERNS] fetchAuthorPatterns data:', { itemCount: data.items?.length })
  return data
}

export async function searchPatternsSimple(
  query: string,
  limit: number = 10,
  page: number = 1,
): Promise<PatternSearchResult[]> {
  const resp = await fetch(`${API_BASE}/patterns/search?q=${encodeURIComponent(query)}&limit=${limit}&page=${page}`)
  if (!resp.ok) throw new Error('Failed to search patterns')
  const data: SearchResponse = await resp.json() as SearchResponse
  return data.items.flatMap((r) => (r.pattern ? [r.pattern] : []))
}

export async function fetchPatternCraftProjects(patternId: string, limit: number = 6): Promise<(ProjectSearchDocument & { _score: number })[]> {
  const resp = await fetch(`${API_BASE}/projects/search?pattern_id=${encodeURIComponent(patternId)}&limit=${limit}`)
  if (!resp.ok) throw new Error('Failed to fetch pattern craft projects')
  const data: SearchProjectsResponse = await resp.json() as SearchProjectsResponse
  return data.items || []
}

export async function fetchPatternsUsingMaterial(
  materialId: string,
  limit: number = 6,
): Promise<MaterialPagePatternUsage[]> {
  const resp = await fetch(`${API_BASE}/materials/${encodeURIComponent(materialId)}/patterns?limit=${limit}`)
  if (!resp.ok) throw new Error('Failed to fetch patterns using material')
  const data: MaterialPagePatternsResponse = await resp.json() as MaterialPagePatternsResponse
  return data.patterns || []
}
